"use client"

import Link from "next/link"
import Image from "next/image"
import { ExternalLink, Mail, Phone, Instagram } from "lucide-react"
import { siteConfig } from "@/config/site"
import { NewsletterForm } from "@/components/ui/NewsletterForm"
import { usePathname } from "next/navigation"
import { isValidLocale, type Locale } from "@/lib/i18n"
import { getTranslations } from "@/lib/translations"

type FooterLink = {
  href: string
  label: string
  external?: boolean
}

function getFooterLinks(locale: Locale): {
  webGelistirme: FooterLink[]
  mobilMasaustu: FooterLink[]
  eTicaret: FooterLink[]
  otomasyonAI: FooterLink[]
  kurumsal: FooterLink[]
  kaynaklar: FooterLink[]
} {
  const t = getTranslations(locale)
  return {
    webGelistirme: [
      { href: `/${locale}/${locale === 'tr' ? 'hizmetler/web-tasarim' : 'services/web-design'}`, label: locale === 'tr' ? "Web Tasarım" : "Web Design" },
      { href: `/${locale}/${locale === 'tr' ? 'hizmetler/web-yazilim' : 'services/web-development'}`, label: locale === 'tr' ? "Web Yazılım" : "Web Development" },
      { href: `/${locale}/${locale === 'tr' ? 'hizmetler/ui-ux-tasarim' : 'services/ui-ux-design'}`, label: locale === 'tr' ? "UI & UX Tasarım" : "UI & UX Design" },
    ],
    mobilMasaustu: [
      { href: `/${locale}/${locale === 'tr' ? 'hizmetler/mobil-uygulama' : 'services/mobile-app'}`, label: locale === 'tr' ? "Mobil Uygulama" : "Mobile App" },
      { href: `/${locale}/${locale === 'tr' ? 'hizmetler/masaustu-uygulama' : 'services/desktop-app'}`, label: locale === 'tr' ? "Masaüstü Uygulama" : "Desktop App" },
    ],
    eTicaret: [
      { href: `/${locale}/${locale === 'tr' ? 'hizmetler/e-ticaret' : 'services/e-commerce'}`, label: locale === 'tr' ? "E-ticaret" : "E-Commerce" },
      { href: `/${locale}/${locale === 'tr' ? 'hizmetler/shopify' : 'services/shopify'}`, label: "Shopify" },
      { href: `/${locale}/${locale === 'tr' ? 'hizmetler/woocommerce' : 'services/woocommerce'}`, label: "WooCommerce" },
    ],
    otomasyonAI: [
      { href: `/${locale}/${locale === 'tr' ? 'hizmetler/otomasyon' : 'services/automation'}`, label: locale === 'tr' ? "Otomasyon" : "Automation" },
      { href: `/${locale}/${locale === 'tr' ? 'hizmetler/ai-servisler' : 'services/ai-services'}`, label: locale === 'tr' ? "AI Servisler" : "AI Services" },
    ],
    kurumsal: [
      { href: `/${locale}/${locale === 'tr' ? 'hakkimizda' : 'about'}`, label: locale === 'tr' ? "Hakkımızda" : "About Us" },
      { href: `/${locale}/${locale === 'tr' ? 'kurumsal-kimlik' : 'corporate-identity'}`, label: locale === 'tr' ? "Kurumsal Kimlik" : "Corporate Identity" },
      { href: `/${locale}/laki`, label: locale === 'tr' ? "Laki ile Tanışın" : "Meet Laki" },
      { href: `/${locale}/${locale === 'tr' ? 'iletisim' : 'contact'}`, label: locale === 'tr' ? "İletişim" : "Contact" },
      { href: `/${locale}/${locale === 'tr' ? 'teklif-al' : 'quote-request'}`, label: locale === 'tr' ? "Teklif Al" : "Get Quote" },
    ],
    kaynaklar: [
      { href: `/${locale}/${locale === 'tr' ? 'hizmetler' : 'services'}`, label: locale === 'tr' ? "Hizmetler" : "Services" },
      { href: `/${locale}/${locale === 'tr' ? 'referanslar' : 'references'}`, label: locale === 'tr' ? "Referanslar" : "References" },
      { href: `/${locale}/blog`, label: "Blog" },
    ],
  }
}

export function Footer() {
  const pathname = usePathname()
  const currentYear = new Date().getFullYear()
  
  // Get current locale from pathname
  // Note: usePathname() only works on client, so we need to handle SSR differently
  const getCurrentLocale = (): Locale => {
    // During SSR, pathname might not be available, so we default to 'tr'
    // This will be corrected on the client side
    if (!pathname || pathname === '/') return 'tr'
    
    const segments = pathname.split('/').filter(Boolean)
    const firstSegment = segments[0]
    
    if (isValidLocale(firstSegment)) {
      return firstSegment
    }
    
    return 'tr'
  }

  const currentLocale = getCurrentLocale()
  const t = getTranslations(currentLocale)
  const footerLinks = getFooterLinks(currentLocale)

  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-12 lg:pb-16 pt-0" suppressHydrationWarning>
        {/* Top Section - Logo and Contact */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-12 pb-8 border-b border-slate-800 pt-8 sm:pt-8" suppressHydrationWarning>
          <div className="sm:mb-0 flex items-center mb-4 sm:mb-0 max-w-[192px]" suppressHydrationWarning>
            <Image
              src="/LAKVERSE-white.png"
              alt="LAKVERSE"
              width={192}
              height={64}
              className="h-8 sm:h-10 md:h-12 w-auto"
              quality={70}
              sizes="(max-width: 640px) 128px, (max-width: 768px) 160px, 192px"
            />
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6" suppressHydrationWarning>
            {/* İletişim Bilgileri */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4" suppressHydrationWarning>
              {siteConfig.links.email && (
                <a
                  href={`mailto:${siteConfig.links.email}`}
                  className="text-slate-300 hover:text-white transition-colors flex items-center gap-2 text-sm sm:text-base"
                  aria-label="E-posta"
                >
                  <Mail className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>{siteConfig.links.email}</span>
                </a>
              )}
              {siteConfig.links.phone && (
                <a
                  href={`tel:${siteConfig.links.phone.replace(/\s/g, '')}`}
                  className="text-slate-300 hover:text-white transition-colors flex items-center gap-2 text-sm sm:text-base"
                  aria-label="Telefon"
                >
                  <Phone className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>{siteConfig.links.phone}</span>
                </a>
              )}
            </div>
            
            {/* Sosyal Medya */}
            <div className="flex items-center gap-3 sm:gap-4" suppressHydrationWarning>
              {siteConfig.social.instagram && (
                <a
                  href={siteConfig.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-white transition-colors flex items-center gap-2"
                  aria-label="Instagram"
                >
                  <Instagram className="h-5 w-5" />
                  <span className="text-sm sm:text-base">Instagram</span>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Links Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 md:gap-12 mb-12" suppressHydrationWarning>
          {/* Web Geliştirme + E-Ticaret */}
          <div className="space-y-8" suppressHydrationWarning>
            <div suppressHydrationWarning>
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
                {t.footer.sections.webDevelopment}
              </h2>
              <ul className="space-y-3">
                {footerLinks.webGelistirme.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-sm text-slate-300 hover:text-white transition-colors flex items-center gap-1 group"
                      suppressHydrationWarning
                    >
                      {link.label}
                      {link.external && (
                        <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div suppressHydrationWarning>
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
                {t.footer.sections.eCommerce}
              </h2>
              <ul className="space-y-3">
                {footerLinks.eTicaret.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-sm text-slate-300 hover:text-white transition-colors flex items-center gap-1 group"
                      suppressHydrationWarning
                    >
                      {link.label}
                      {link.external && (
                        <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Mobil & Masaüstü + Otomasyon & AI */}
          <div className="space-y-8" suppressHydrationWarning>
            <div suppressHydrationWarning>
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
                {t.footer.sections.mobileDesktop}
              </h2>
              <ul className="space-y-3">
                {footerLinks.mobilMasaustu.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-sm text-slate-300 hover:text-white transition-colors flex items-center gap-1 group"
                      suppressHydrationWarning
                    >
                      {link.label}
                      {link.external && (
                        <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div suppressHydrationWarning>
              <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
                {t.footer.sections.automationAI}
              </h2>
              <ul className="space-y-3">
                {footerLinks.otomasyonAI.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-sm text-slate-300 hover:text-white transition-colors flex items-center gap-1 group"
                      suppressHydrationWarning
                    >
                      {link.label}
                      {link.external && (
                        <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Kurumsal */}
          <div suppressHydrationWarning>
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
              {t.footer.sections.corporate}
            </h2>
            <ul className="space-y-3">
              {footerLinks.kurumsal.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-slate-300 hover:text-white transition-colors flex items-center gap-1 group"
                    suppressHydrationWarning
                  >
                    {link.label}
                    {link.external && (
                      <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Kaynaklar */}
          <div suppressHydrationWarning>
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
              {t.footer.sections.resources}
            </h2>
            <ul className="space-y-3">
              {footerLinks.kaynaklar.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-slate-300 hover:text-white transition-colors flex items-center gap-1 group"
                    suppressHydrationWarning
                  >
                    {link.label}
                    {link.external && (
                      <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter Subscription */}
          <div className="col-span-2 lg:col-span-2" suppressHydrationWarning>
            <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
              {currentLocale === 'tr' ? 'Haber Bülteni' : 'Newsletter'}
            </h2>
            <p className="text-sm text-slate-400 mb-4">
              {currentLocale === 'tr' 
                ? 'Yeni projelerimiz, blog yazılarımız ve özel tekliflerden haberdar olmak için abone olun.'
                : 'Subscribe to stay informed about our new projects, blog posts, and special offers.'}
            </p>
            <NewsletterForm />
          </div>
        </div>

        {/* Bottom Section */}
        <div className="pt-8 border-t border-slate-800" suppressHydrationWarning>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4" suppressHydrationWarning>
            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400" suppressHydrationWarning>
              <Link
                href={`/${currentLocale}/${currentLocale === 'tr' ? 'gizlilik' : 'privacy'}`}
                className="hover:text-white transition-colors"
              >
                {t.footer.privacyPolicy}
              </Link>
              <Link
                href={`/${currentLocale}/${currentLocale === 'tr' ? 'kvkk' : 'data-protection-law'}`}
                className="hover:text-white transition-colors"
              >
                {t.footer.kvkk}
              </Link>
              <Link
                href={`/${currentLocale}/${currentLocale === 'tr' ? 'kullanim-kosullari' : 'terms-of-use'}`}
                className="hover:text-white transition-colors"
              >
                {t.footer.termsOfUse}
              </Link>
              <Link
                href={`/${currentLocale}/${currentLocale === 'tr' ? 'cerez-politikasi' : 'cookie-policy'}`}
                className="hover:text-white transition-colors"
              >
                {t.footer.cookiePolicy}
              </Link>
            </div>
            <div className="text-xs text-slate-300" suppressHydrationWarning>
              {t.footer.copyright} {currentYear} {siteConfig.name.toUpperCase()}. {t.footer.allRightsReserved}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
